import { CommonModule } from '@angular/common'
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { AlpacaAccountInterface } from '@modules/alpaca-module/alpaca-account/interfaces/alpaca-account.interface'
import { TranslatePipe } from '@ngx-translate/core'
import { TuiExpand, TuiIcon } from '@taiga-ui/core'
import { Subject, takeUntil } from 'rxjs'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { TuiBadge, TuiSkeleton } from '@taiga-ui/kit'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { PortfolioSettingsInterface } from '@shared/components/trading/portfolio/core/interfaces/portfolio-settings.interface'
import { PortfolioBlocksEnum } from '@shared/components/trading/portfolio/core/enums/portfolio-blocks.enum'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import { PricePipe } from '@shared/pipes/price.pipe'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { PositionsStocksHelperService } from '@modules/stocks-module/positions/services/positions-stocks-helper.service'
import { AlpacaAccountHelperService } from '@modules/alpaca-module/alpaca-account/services/alpaca-account-helper.service'
import { SecretDirective } from '@shared/directives/secret.directive'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { AnimatedNumberDirective } from '@shared/directives/animated-number/animated-number.directive'
import { RiskLevelEnum } from '@modules/alpaca-module/alpaca-account/enums/risk-level.enum'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-portfolio',
	templateUrl: './portfolio.component.html',
	styleUrl: './portfolio.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslatePipe,
		TuiIcon,
		TuiExpand,
		HighlightDirective,
		TuiBadge,
		TuiSkeleton,
		PricePipe,
		ProfitPipe,
		SecretDirective,
		ButtonComponent,
		AnimatedNumberDirective,
	],
})
export class PortfolioComponent implements OnInit, OnDestroy {
	protected readonly number: NumberConstructor = Number
	protected readonly array: ArrayConstructor = Array
	protected readonly portfolioBlocksEnum: typeof PortfolioBlocksEnum = PortfolioBlocksEnum
	protected readonly riskLevelEnum: typeof RiskLevelEnum = RiskLevelEnum

	public widget: InputSignal<WidgetInterface<PortfolioSettingsInterface> | null> =
		input<WidgetInterface<PortfolioSettingsInterface> | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)
	private readonly _positionsStocksHelperService: PositionsStocksHelperService =
		inject<PositionsStocksHelperService>(PositionsStocksHelperService)
	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _alpacaAccountHelperService: AlpacaAccountHelperService =
		inject<AlpacaAccountHelperService>(AlpacaAccountHelperService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	protected alpacaAccount: WritableSignal<AlpacaAccountInterface | null> = signal<AlpacaAccountInterface | null>(null)
	protected isLoadingPositions: WritableSignal<boolean> = signal<boolean>(false)
	protected isLoadingPortfolioHistory: WritableSignal<boolean> = signal<boolean>(false)
	protected isSecret: WritableSignal<boolean> = signal<boolean>(false)

	protected badgeAppearance: Signal<string> = computed<string>((): string => {
		return this._alpacaAccountHelperService.getBadgeAppearanceByRiskLevel(
			this.alpacaAccount()?.riskLevel ?? this.riskLevelEnum.low,
		)
	})

	protected titleMarginLevelPath: Signal<string> = computed<string>((): string => {
		switch (this.alpacaAccount()?.riskLevel) {
			case this.riskLevelEnum.low:
				return 'portfolio.riskLevelsTitles.low'
			case this.riskLevelEnum.moderate:
				return 'portfolio.riskLevelsTitles.moderate'
			case this.riskLevelEnum.high:
				return 'portfolio.riskLevelsTitles.high'
			default:
				return 'portfolio.riskLevelsTitles.low'
		}
	})

	get marginLevelStates(): boolean[] {
		return this.array.from(
			{ length: 6 },
			(_: unknown, i: number): boolean => i < (this.alpacaAccount()?.activeMarginLevel ?? 0),
		)
	}
	private _listenSecret(): void {
		this._userHelperService.isSecret$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			this.isSecret.set(value)
		})
	}

	private _initPositionsParams(): void {
		this._positionsStocksHelperService.positionsParams$.next({
			page: 1,
			pageSize: 100,
		})
	}

	private _launchPositions(): void {
		this._tradingLauncherService
			.launchPositions$()
			.pipe(takeUntil(this._destroy$))
			.subscribe(([, loading]: [PositionsInterface[], boolean]): void => {
				this.isLoadingPositions.set(loading)
			})
	}

	private _listenLoadingPortfolioHistory(): void {
		this._alpacaAccountHelperService.isLoadingPortfolioHistory$
			.pipe(takeUntil(this._destroy$))
			.subscribe((response: boolean): void => {
				this.isLoadingPortfolioHistory.set(response)
			})
	}

	private _listenAlpacaAccount(): void {
		this._alpacaAccountHelperService.alpacaAccount$
			.pipe(takeUntil(this._destroy$))
			.subscribe((alpacaAccount: AlpacaAccountInterface | null): void => {
				this.alpacaAccount.set(alpacaAccount)
			})
	}

	protected toggleExpand(block: PortfolioBlocksEnum): void {
		const widget: WidgetInterface<PortfolioSettingsInterface> | null = this.widget()
		if (!widget) return

		const expandedBlocks: PortfolioBlocksEnum[] = widget.settings.expanded.includes(block)
			? widget.settings.expanded.filter((b: PortfolioBlocksEnum): boolean => b !== block)
			: [...widget.settings.expanded, block]

		this._widgetsService.updateWidget({
			...widget,
			settings: {
				...widget.settings,
				expanded: expandedBlocks,
			},
		})
	}

	protected checkIsVisible(block: PortfolioBlocksEnum): boolean {
		const widget: WidgetInterface<PortfolioSettingsInterface> | null = this.widget()
		if (!widget) return false

		return widget.settings.visible.includes(block)
	}

	protected checkIsExpanded(block: PortfolioBlocksEnum): boolean {
		const widget: WidgetInterface<PortfolioSettingsInterface> | null = this.widget()
		if (!widget) return false

		return widget.settings.expanded.includes(block)
	}

	protected toggleSecret(): void {
		this._userHelperService.toggleSecret()
	}

	ngOnInit(): void {
		this._listenSecret()
		this._listenAlpacaAccount()
		this._initPositionsParams()
		this._launchPositions()
		this._listenLoadingPortfolioHistory()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
